import moment from 'moment';
import { ReactNode } from 'react';
import { fuzzyStringContains } from '../../../utils/strings';
import { DataTableCellContent, DataTableProps } from '../types';
import { getPlainText } from '../plainText';

type SearchFunctionType = 'date' | 'text';

type SearchFunction = (value: ReactNode, searchTerm: string) => boolean;

const searchDate: SearchFunction = (value, searchTerm) => {
  if (typeof value !== 'string') {
    return false;
  }

  const possibleDateValue = moment(value);

  if (!possibleDateValue.isValid()) {
    return false;
  }

  const possibleDateSearch = moment(searchTerm);

  if (!possibleDateSearch.isValid()) {
    return false;
  }

  return possibleDateValue.isSame(possibleDateSearch, 'date');
};

const searchText: SearchFunction = (value, searchTerm) => {
  if (typeof value !== 'string') {
    return false;
  }

  return fuzzyStringContains(value, searchTerm);
};

const searchFunctionMapping: Record<SearchFunctionType, SearchFunction> = {
  date: searchDate,
  text: searchText
};

const getSearchFunction = <T extends string>(
  searchableColumns: DataTableProps<T>['searchableColumns'],
  columnKey: T
): SearchFunction | undefined => {
  const searchType: SearchFunctionType | undefined =
    searchableColumns?.[columnKey];

  if (!searchType) {
    return undefined;
  }

  return searchFunctionMapping[searchType];
};

export const searchData = <T extends string>(
  data: DataTableCellContent<T>[],
  columns: DataTableProps<T>['columns'],
  searchableColumns: DataTableProps<T>['searchableColumns'],
  searchTerm: string
) => {
  if (!searchTerm) {
    return data;
  }

  return data.filter(row =>
    columns.some(column => {
      const searchFunction = getSearchFunction(searchableColumns, column);

      if (!searchFunction) {
        return false;
      }

      return searchFunction(getPlainText(row[column]), searchTerm);
    })
  );
};
